import React, { useState, useEffect } from 'react';
import { useNotification } from '../../contexts/NotificationContext';
import { adminService } from '../../services';
import { ChevronLeft, UserCheck } from 'lucide-react';
import { LoadingPage } from '../../components/ui/Loading';
import { getClassTypeIcon, getClassTypeTranslation } from '../../utils/characterTranslation';

const AdminEnlistments = () => {
  const { notify } = useNotification();
  const [pendingUsers, setPendingUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('pending');
  const [searchTerm, setSearchTerm] = useState('');
  const [rejectModal, setRejectModal] = useState({ open: false, userId: null, userName: '' });
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      
      if (activeTab === 'pending') {
        const data = await adminService.getPendingUsers();
        setPendingUsers(data.users || []);
      } else {
        const data = await adminService.getAllUsers();
        setAllUsers(data.users || []);
      }
    } catch (err) {
      console.error('Error loading users:', err);
      setError('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (userId, action) => {
    try {
      setActionLoading(prev => ({ ...prev, [userId]: action }));
      setError('');

      let response;
      switch (action) {
        case 'approve':
          response = await adminService.approveUser(userId);
          break;
        case 'reject':
          response = await adminService.rejectUser(userId, rejectionReason);
          closeRejectModal();
          break;
        case 'suspend':
          response = await adminService.suspendUser(userId);
          break;
        default:
          return;
      }

      const actionLabels = {
        approve: 'aprovação',
        reject: 'rejeição',  
        suspend: 'suspensão'
      };
      notify.success(response.message || `Ação de ${actionLabels[action]} realizada com sucesso`, 'Ação Concluída');
      loadData();
    } catch (err) {
      console.error(`Error ${action} user:`, err);
      setError(err.response?.data?.error || `Erro ao ${action} usuário`);
    } finally {
      setActionLoading(prev => ({ ...prev, [userId]: null }));
    }
  };

  const openRejectModal = (userId, userName) => {
    setRejectModal({ open: true, userId, userName });
    setRejectionReason('');
  };

  const closeRejectModal = () => {
    setRejectModal({ open: false, userId: null, userName: '' });
    setRejectionReason('');
  };

  const handleRejectSubmit = () => {
    if (!rejectionReason.trim()) {
      notify.error('Por favor, informe o motivo da rejeição', 'Campo Obrigatório');
      return;
    }
    handleAction(rejectModal.userId, 'reject');
  };

  const sendWhatsApp = (phone) =>{
      let newPhone = phone.replace(/[^0-9]/g,'');

      if (newPhone.length <= 11){
          newPhone = '55' + newPhone;
      }

      window.open(`https://wa.me/+${newPhone}?text=Ol%C3%A1%2C%20obrigado%20pelo%20seu%20interesse%20em%20participar%20da%20Revolution%2C%20estou%20iniciando%20seu%20recrutamento%2C%20talvez%20precise%20te%20fazer%20umas%20perguntas`)
  }

  const handleStartRecruitment = async (userId) => {
    try {
      setActionLoading(prev => ({ ...prev, [userId]: 'recruiting' }));
      const response = await adminService.startRecruitment(userId);
      notify.success(response.message || 'Recrutamento iniciado!', 'Recrutamento');
      loadData();
    } catch (err) {
      if (err.response?.status === 409) {
        notify.warning(
          err.response.data.error || 'Recrutamento já iniciado por outro recrutador',
          'Atenção'
        );
        loadData();
      } else {
        notify.error(err.response?.data?.error || 'Erro ao iniciar recrutamento', 'Erro');
      }
    } finally {
      setActionLoading(prev => ({ ...prev, [userId]: null }));
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      PENDING: 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30',
      APPROVED: 'bg-green-500/20 text-green-400 border border-green-500/30',
      REJECTED: 'bg-red-500/20 text-red-400 border border-red-500/30',
      SUSPENDED: 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
    };
    
    const texts = {
      PENDING: '⏳ Pendente',
      APPROVED: '✅ Aprovado',
      REJECTED: '❌ Rejeitado',
      SUSPENDED: '🚫 Suspenso'
    };

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-lg text-xs font-semibold ${styles[status]}`}>
        {texts[status]}
      </span>
    );
  };

  const filteredUsers = (activeTab === 'pending' ? pendingUsers : allUsers).filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <LoadingPage message="Carregando alistamentos..." title="Gerenciamento de Usuários" />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-xl">
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={() => window.location.href = '/app/admin'}
              className="flex items-center justify-center w-10 h-10 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-purple-500/50 rounded-lg text-gray-300 hover:text-white transition-all duration-300"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="flex-1">
              <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent mb-1">
                🎖️ Gerenciar Alistamentos
              </h1>
              <p className="text-gray-300">
                Aprove ou rejeite solicitações de alistamento de novos membros
              </p>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="flex gap-3">
            <button
              onClick={() => setActiveTab('pending')}
              className={`flex-1 sm:flex-none px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                activeTab === 'pending'
                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg shadow-purple-500/20'
                  : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white border border-white/10'
              }`}
            >
              ⏳ Pendentes ({pendingUsers.length})
            </button>
            <button
              onClick={() => setActiveTab('all')}
              className={`flex-1 sm:flex-none px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                activeTab === 'all'
                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg shadow-purple-500/20'
                  : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white border border-white/10'
              }`}
            >
              👥 Todos os Usuários
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-xl">
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Buscar Usuário
          </label>
          <input
            type="text"
            placeholder="Nome ou email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2.5 bg-slate-700/50 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
          />
        </div>

        {error && (
          <div className="bg-gradient-to-br from-red-900/20 to-red-800/20 backdrop-blur-xl rounded-2xl p-4 border border-red-500/20 shadow-xl">
            <p className="text-red-400 text-center">{error}</p>
          </div>
        )}

        {/* Users List */}
        {filteredUsers.length === 0 ? (
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-12 border border-white/10 shadow-xl text-center">
            <div className="text-6xl mb-4">
              {activeTab === 'pending' ? '📝' : '👥'}
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">
              {activeTab === 'pending' ? 'Nenhum alistamento pendente' : 'Nenhum usuário encontrado'}
            </h3>
            <p className="text-gray-400">
              {searchTerm 
                ? 'Tente ajustar os filtros de busca'
                : activeTab === 'pending' 
                  ? 'Todos os alistamentos foram processados'
                  : 'Nenhum usuário cadastrado ainda'
              }
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredUsers.map((user) => (
              <div 
                key={user.id}
                className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-purple-500/50 transition-all duration-300 shadow-xl"
              >
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-4 mb-3">
                      <div className="w-14 h-14 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center shadow-lg">
                        <span className="text-white font-bold text-xl">
                          {user.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-white mb-1">
                          {user.name}
                        </h3>
                        <p className="text-gray-400 text-sm">
                          📧 {user.email}
                        </p>
                        {user.phone && (
                          <p className="text-gray-400 text-sm">
                            📞 {user.phone}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {/* Character Info */}
                    {user.characters && user.characters.length > 0 && (
                      <div className="mb-3 p-3 bg-white/5 rounded-lg border border-white/10">
                        <h4 className="text-xs font-semibold text-gray-400 uppercase mb-2">
                          ⚔️ Personagem Principal
                        </h4>
                        <div className="flex items-center gap-3">
                          <div className="flex-1">
                            <p className="text-white font-medium">
                              {user.characters[0].name}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs px-2 py-1 bg-purple-500/20 text-purple-400 border border-purple-500/30 rounded">
                                {getClassTypeIcon(user.characters[0].class)} {getClassTypeTranslation(user.characters[0].class)}
                              </span>
                              <span className="text-xs text-gray-400">
                                Nível {user.characters[0].level}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Recruitment Info */}
                    {(user.previousClan || user.reasonForLeaving || user.reasonToJoin) && (
                      <div className="mb-3 p-3 bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-lg border border-purple-500/20">
                        <h4 className="text-xs font-semibold text-purple-400 uppercase mb-2">
                          📝 Informações de Recrutamento
                        </h4>
                        <div className="space-y-2 text-sm">
                          {user.previousClan && (
                            <div>
                              <span className="text-white font-medium">Último Clã:</span>{' '}
                              <span className="text-gray-400">{user.previousClan}</span>
                            </div>
                          )}
                          {user.reasonForLeaving && (
                            <div>
                              <span className="text-white font-medium">Motivo da saída:</span>
                              <p className="text-gray-400 mt-1 italic">&quot;{user.reasonForLeaving}&quot;</p>
                            </div>
                          )}
                          {user.reasonToJoin && (
                            <div>
                              <span className="text-white font-medium">Por que quer entrar:</span>
                              <p className="text-gray-400 mt-1 italic">&quot;{user.reasonToJoin}&quot;</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    <div className="flex flex-wrap items-center gap-3 text-sm">
                      <span className="text-gray-400">
                        📅 {new Date(user.createdAt).toLocaleDateString('pt-BR')}
                      </span>
                      {getStatusBadge(user.status)}
                      {user.approver && (
                        <span className="text-gray-400">
                          👤 Por: <span className="text-gray-300 font-medium">{user.approver.name}</span>
                        </span>
                      )}
                    </div>

                    {/* Recruitment Status */}
                    {user.status === 'PENDING' && (
                      <div className="mt-3 flex items-center gap-4">
                        {user.recruitmentStartedBy ? (
                          <div className="flex items-center gap-3 p-3 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
                            <UserCheck className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                            <div className="flex-1">
                              <p className="text-sm text-cyan-300 font-medium">
                                Recrutamento em andamento
                              </p>
                              <p className="text-xs text-gray-400 mt-0.5">
                                Iniciado por <span className="text-white font-medium">{user.recruiter?.name || 'Recrutador'}</span>
                                {user.recruitmentStartedAt && (
                                  <span> em {new Date(user.recruitmentStartedAt).toLocaleDateString('pt-BR')} às {new Date(user.recruitmentStartedAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span>
                                )}
                              </p>
                            </div>
                          </div>
                        ) : (
                          <button
                            onClick={() => handleStartRecruitment(user.id)}
                            disabled={actionLoading[user.id]}
                            className="flex items-center gap-2 px-4 py-2.5 bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-500/30 hover:border-cyan-500/50 rounded-lg text-cyan-400 hover:text-cyan-300 font-medium transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
                          >
                            <UserCheck className="w-4 h-4" />
                            {actionLoading[user.id] === 'recruiting' ? 'Iniciando...' : 'Iniciar Recrutamento'}
                          </button>
                        )}
                        <button
                          onClick={() => handleAction(user.id, 'approve')}
                          disabled={actionLoading[user.id]}
                          className="flex items-center gap-2 px-4 py-2.5 bg-green-500/20 hover:bg-green-500/30 border border-green-500/30 hover:border-green-500/50 rounded-lg text-green-400 hover:text-green-300 font-medium transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {actionLoading[user.id] === 'approve' ? '⏳ Aprovando...' : '✅ Aprovar'}
                        </button>
                        <button
                          onClick={() => openRejectModal(user.id, user.name)}
                          disabled={actionLoading[user.id]}
                          className="flex items-center gap-2 px-4 py-2.5 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 hover:border-red-500/50 rounded-lg text-red-400 hover:text-red-300 font-medium transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          ❌ Rejeitar
                        </button>
                        <button
                          onClick={() => sendWhatsApp(user.phone)}
                          disabled={actionLoading[user.id]}
                          className="flex items-center gap-2 px-4 py-2.5 bg-green-700/20 hover:bg-green-700/30 border border-green-700/30 hover:border-green-700/50 rounded-lg text-green-400 hover:text-green-300 font-medium transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          🕻 Chamar no WhatsApp
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Reject Modal */}
      {rejectModal.open && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-6 max-w-lg w-full border border-red-500/30 shadow-2xl">
            <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <span className="text-3xl">❌</span>
              Rejeitar Alistamento
            </h3>
            
            <p className="text-gray-300 mb-2">
              Você está prestes a rejeitar o alistamento de:
            </p>
            <p className="text-white font-bold text-lg mb-4">
              {rejectModal.userName}
            </p>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Motivo da Rejeição <span className="text-red-400">*</span>
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Explique o motivo da rejeição (ex: perfil incompleto, não atende requisitos, etc.)"
                rows="4"
                className="w-full px-4 py-3 bg-slate-700/50 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
              />
              <p className="text-xs text-gray-400 mt-2">
                ℹ️ Este motivo será enviado por email ao candidato
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={closeRejectModal}
                className="flex-1 px-4 py-3 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 rounded-lg text-gray-300 hover:text-white font-medium transition-all duration-300"
              >
                Cancelar
              </button>
              <button
                onClick={handleRejectSubmit}
                disabled={!rejectionReason.trim() || actionLoading[rejectModal.userId]}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 rounded-lg text-white font-medium transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-red-500/20"
              >
                {actionLoading[rejectModal.userId] ? '⏳ Rejeitando...' : 'Confirmar Rejeição'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminEnlistments;